import { logger } from './logger.js';

const CHART_MODES = ['cashflow', 'receivables', 'contract_backlog'];
const CASHFLOW_DAYS = [30, 45, 60];
const RECEIVABLES_MAX_AGE_DAYS = [730, 365, 182, 91];
const BACKLOG_MONTHS = [3, 6, 12];
const RECEIVABLES_SORT_COLUMNS = ['customer_name', 'overdue_amount'];
const PROJECT_TABS = ['active', 'inactive', 'no_activity', 'closed', 'internal'];
const PROJECT_SORT_COLUMNS = [
  'source_system_project_number',
  'project_performance_category',
  'project_name',
  'customer_name',
  'contribution',
  'remaining_contract_value',
  'contract_value',
];
const SORT_DIRECTIONS = ['asc', 'desc'];

const DEFAULT_OVERVIEW = {
  chartMode: 'cashflow',
  cashflowDays: 30,
  receivablesMaxAgeDays: 365,
  receivablesSortColumn: 'overdue_amount',
  receivablesSortDirection: 'desc',
  backlogMonths: 3,
  includeNonInvoicedWork: false,
  includeOverdueInvoices: false,
};

const DEFAULT_PROJECTS = {
  activeTab: 'active',
  sortColumn: 'contribution',
  sortDirection: 'asc',
};

const DEFAULT_AGENT_OPEN = true;

/**
 * localStorage key for one user's workspace document.
 * @param {string} userId
 * @returns {string}
 */
function storageKey(userId) {
  return `revetall-workspace-${userId}`;
}

/**
 * @param {unknown} value
 * @param {string[]} allowed
 * @param {string} fallback
 * @returns {string}
 */
function oneOf(value, allowed, fallback) {
  return typeof value === 'string' && allowed.includes(value) ? value : fallback;
}

/**
 * @param {unknown} value
 * @param {number[]} allowed
 * @param {number} fallback
 * @returns {number}
 */
function oneOfNumber(value, allowed, fallback) {
  return typeof value === 'number' && allowed.includes(value) ? value : fallback;
}

/**
 * @param {unknown} value
 * @param {boolean} fallback
 * @returns {boolean}
 */
function asBoolean(value, fallback) {
  return typeof value === 'boolean' ? value : fallback;
}

/**
 * Read the raw workspace document. Invalid JSON or a missing key returns defaults.
 * Company sections are left unsanitized so a write of one section keeps the others.
 * @param {string} userId
 * @returns {{ agentOpen: boolean, companies: Object }}
 */
function readRaw(userId) {
  if (!userId) {
    return { agentOpen: DEFAULT_AGENT_OPEN, companies: {} };
  }
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) {
      return { agentOpen: DEFAULT_AGENT_OPEN, companies: {} };
    }
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return { agentOpen: DEFAULT_AGENT_OPEN, companies: {} };
    }
    const companies = parsed.companies && typeof parsed.companies === 'object' && !Array.isArray(parsed.companies)
      ? parsed.companies
      : {};
    return {
      agentOpen: asBoolean(parsed.agentOpen, DEFAULT_AGENT_OPEN),
      companies,
    };
  } catch (error) {
    logger.warn('Failed to parse workspace state:', error);
    return { agentOpen: DEFAULT_AGENT_OPEN, companies: {} };
  }
}

/**
 * @param {string} userId
 * @param {{ agentOpen: boolean, companies: Object }} doc
 */
function writeRaw(userId, doc) {
  if (!userId) return;
  localStorage.setItem(storageKey(userId), JSON.stringify(doc));
}

/**
 * @param {string} userId
 * @param {string|number} companyId
 * @returns {Object|null}
 */
function companySection(userId, companyId) {
  if (!userId || companyId == null) return null;
  const section = readRaw(userId).companies[String(companyId)];
  if (!section || typeof section !== 'object' || Array.isArray(section)) return null;
  return section;
}

/**
 * Replace one company section and leave agentOpen and other companies unchanged.
 * @param {string} userId
 * @param {string|number} companyId
 * @param {string} section
 * @param {Object} value
 */
function updateCompanySection(userId, companyId, section, value) {
  if (!userId || companyId == null) return;
  const doc = readRaw(userId);
  const key = String(companyId);
  const existing = doc.companies[key];
  const company = existing && typeof existing === 'object' && !Array.isArray(existing)
    ? { ...existing }
    : {};
  company[section] = value;
  writeRaw(userId, {
    agentOpen: doc.agentOpen,
    companies: {
      ...doc.companies,
      [key]: company,
    },
  });
}

/**
 * @param {Object|null|undefined} overview
 * @returns {Object} Overview fields limited to known values
 */
function sanitizeOverview(overview) {
  const source = overview && typeof overview === 'object' ? overview : {};
  return {
    chartMode: oneOf(source.chartMode, CHART_MODES, DEFAULT_OVERVIEW.chartMode),
    cashflowDays: oneOfNumber(source.cashflowDays, CASHFLOW_DAYS, DEFAULT_OVERVIEW.cashflowDays),
    receivablesMaxAgeDays: oneOfNumber(
      source.receivablesMaxAgeDays,
      RECEIVABLES_MAX_AGE_DAYS,
      DEFAULT_OVERVIEW.receivablesMaxAgeDays
    ),
    receivablesSortColumn: oneOf(
      source.receivablesSortColumn,
      RECEIVABLES_SORT_COLUMNS,
      DEFAULT_OVERVIEW.receivablesSortColumn
    ),
    receivablesSortDirection: oneOf(
      source.receivablesSortDirection,
      SORT_DIRECTIONS,
      DEFAULT_OVERVIEW.receivablesSortDirection
    ),
    backlogMonths: oneOfNumber(source.backlogMonths, BACKLOG_MONTHS, DEFAULT_OVERVIEW.backlogMonths),
    includeNonInvoicedWork: asBoolean(source.includeNonInvoicedWork, DEFAULT_OVERVIEW.includeNonInvoicedWork),
    includeOverdueInvoices: asBoolean(source.includeOverdueInvoices, DEFAULT_OVERVIEW.includeOverdueInvoices),
  };
}

/**
 * @param {Object|null|undefined} projects
 * @returns {Object} Projects fields limited to known values
 */
function sanitizeProjects(projects) {
  const source = projects && typeof projects === 'object' ? projects : {};
  return {
    activeTab: oneOf(source.activeTab, PROJECT_TABS, DEFAULT_PROJECTS.activeTab),
    sortColumn: oneOf(source.sortColumn, PROJECT_SORT_COLUMNS, DEFAULT_PROJECTS.sortColumn),
    sortDirection: oneOf(source.sortDirection, SORT_DIRECTIONS, DEFAULT_PROJECTS.sortDirection),
  };
}

/**
 * Read whether the agent drawer is open for this user.
 * @param {string} [userId]
 * @returns {boolean}
 */
export function readAgentOpen(userId) {
  return readRaw(userId).agentOpen;
}

/**
 * Store whether the agent drawer is open. Does not change company sections.
 * @param {string} userId
 * @param {boolean} open
 */
export function writeAgentOpen(userId, open) {
  if (!userId || typeof open !== 'boolean') return;
  const doc = readRaw(userId);
  writeRaw(userId, { agentOpen: open, companies: doc.companies });
}

/**
 * Read overview setup for one company. Unknown values fall back to defaults.
 * @param {string} [userId]
 * @param {string|number} [companyId]
 * @returns {Object}
 */
export function readOverview(userId, companyId) {
  return sanitizeOverview(companySection(userId, companyId)?.overview);
}

/**
 * Replace the overview section for one company.
 * @param {string} userId
 * @param {string|number} companyId
 * @param {Object} overview
 */
export function writeOverview(userId, companyId, overview) {
  updateCompanySection(userId, companyId, 'overview', sanitizeOverview(overview));
}

/**
 * Read projects setup for one company. Unknown values fall back to defaults.
 * @param {string} [userId]
 * @param {string|number} [companyId]
 * @returns {Object}
 */
export function readProjects(userId, companyId) {
  return sanitizeProjects(companySection(userId, companyId)?.projects);
}

/**
 * Replace the projects section for one company.
 * @param {string} userId
 * @param {string|number} companyId
 * @param {Object} projects
 */
export function writeProjects(userId, companyId, projects) {
  updateCompanySection(userId, companyId, 'projects', sanitizeProjects(projects));
}

/**
 * Read the selected dashboard id for one company.
 * @param {string} [userId]
 * @param {string|number} [companyId]
 * @returns {number|null}
 */
export function readSelectedDashboardId(userId, companyId) {
  const id = companySection(userId, companyId)?.dashboards?.selectedId;
  return Number.isInteger(id) ? id : null;
}

/**
 * Replace the dashboards section for one company.
 * @param {string} userId
 * @param {string|number} companyId
 * @param {number} selectedId
 */
export function writeSelectedDashboardId(userId, companyId, selectedId) {
  if (!Number.isInteger(selectedId)) return;
  updateCompanySection(userId, companyId, 'dashboards', { selectedId });
}
